import React, { FunctionComponent, useState } from 'react';
import { Button, Col, FormGroup, Label, Spinner } from 'reactstrap';
import { ApiDataBinding, withApiData } from 'react-api-data/lib';
import { compose } from 'recompose';
import { RouteComponentProps, withRouter } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';

// constants
import { Roles } from 'constants/literals';

// utils
import { getLinkToContract, getOnlyOneExchangeName } from 'utils/linksUtils';

// components
import { ContractCreateResponse, Exchanges, Organisation } from 'constants/api';
import RolesChecker from 'components/utils/RolesChecker';
import FormControl from 'components/utils/FormControl';
import FormModal from './FormModal';
import { ExchangeFormikSelect } from 'components/utils/ExchangeSelect';
import ReservedForTestingCheckbox from 'components/utils/ReservedForTestingCheckbox';
import { object, string } from 'yup';

interface InProps {
    organisation?: Organisation;
    exchangesData: Exchanges;
}

interface EnhanceProps {
    postContract: ApiDataBinding<ContractCreateResponse>;
}

interface WithStateProps {
    targetExchange: string;
    setTargetExchange: (exchange: string) => void;
}

type Props = EnhanceProps & InProps & WithStateProps & RouteComponentProps & WithTranslation;

type FormValues = {
    erp_customer_identifier: string;
    erp_customer_name: string;
    erp_contract_identifier: string;
    name: string;
    reserved_for_testing: boolean;
    exchange: string;
};

const enhance = compose<Props, InProps>(
    withApiData({ postContract: 'postContract' }),
    withRouter,
    withTranslation()
);

const ContractAddModal: FunctionComponent<Props> = ({ exchangesData, organisation, postContract, history, t }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!exchangesData) return <Spinner data-test="spinner" />;

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }
    function onSubmit(values: FormValues) {
        const { exchange, ...params } = values;
        return postContract.perform({ exchange: values.exchange }, params);
    }

    function onSuccess(binding: ApiDataBinding<{data: ContractCreateResponse}>) {
        const contractUuid = binding.data!.data.id;
        const exchange = binding.request!.params!.exchange.toString();

        history.push(getLinkToContract(exchange, contractUuid));
    }

    const organisationName = organisation ? organisation.name || '' : '';
    const organisationErpId = organisation ? organisation.erp_customer_identifier || '' : '';

    return (
        <React.Fragment>
            <FormModal
                header={t('connection.addNewConnection')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={
                    {
                        erp_customer_identifier: organisationErpId,
                        erp_contract_identifier: '',
                        erp_customer_name: organisationName,
                        name: '',
                        exchange: getOnlyOneExchangeName(exchangesData) || '',
                        reserved_for_testing: false,
                    } as FormValues
                }
                validationSchema={object().shape({
                    erp_customer_name: string().required(t('validation.erpCustomerNameRequired')),
                    exchange: string().required(t('validation.exchangeRequired'))
                })}
                onSubmit={onSubmit}
                onSuccess={onSuccess}
                successMessage={t('notification.customerCreated')}
            >
                {({ errors, touched, values }) => (
                    <React.Fragment>
                        {
                            <>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="erp_customer_name">{t('customers.erpCustomerName')}</Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="erp_customer_name"
                                            disabled={organisationName !== ''}
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="erp_customer_identifier">
                                            {t('customers.erpCustomerIdentifier')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="erp_customer_identifier"
                                            disabled={organisationErpId !== ''}
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="name">{t('customers.name')}</Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl type="text" name="name" errors={errors} touched={touched} />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="erp_contract_identifier">
                                            {t('customers.erpContractIdentifier')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="erp_contract_identifier"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="exchange">{t('common.exchange')}</Label>
                                    </Col>
                                    <Col md={9}>
                                        <ExchangeFormikSelect
                                            name="exchange"
                                            touched={touched}
                                            errors={errors}
                                            exchanges={exchangesData}
                                        />
                                    </Col>
                                </FormGroup>

                                <ReservedForTestingCheckbox checked={values.reserved_for_testing} />
                            </>
                        }
                    </React.Fragment>
                )}
            </FormModal>
            <React.Fragment>
                <RolesChecker data-test="user-roles-checker" roles={[Roles.admin, Roles.noc, Roles.ixaas_client]}>
                    <Button data-test="add-customer-btn" className="float-right" color="light" onClick={toggle}>
                        {t('customers.addCustomerBtn')}
                    </Button>
                </RolesChecker>
            </React.Fragment>
        </React.Fragment>
    );
};

export default enhance(ContractAddModal);
